import { faInfoCircle, faParachuteBox } from '@fortawesome/pro-solid-svg-icons';
import { isPast } from 'date-fns';
import { ReactNode, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import ButtonBase from '@sorare/core/src/atoms/buttons/ButtonBase';
import IconButton from '@sorare/core/src/atoms/buttons/IconButton';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import Scrollable from '@sorare/core/src/atoms/navigation/Scrollable';
import { BodyS } from '@sorare/core/src/atoms/typography';
import { Chip } from '@sorare/core/src/atoms/ui/Chip';
import { buildFilterQuery } from '@sorare/core/src/components/search/InstantSearch/utils';
import {
  FOOTBALL_MARKET_INSTANT_BUY,
  FOOTBALL_MARKET_NEW_SIGNINGS,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIntlContext } from '@sorare/core/src/contexts/intl';
import useScreenSize from '@sorare/core/src/hooks/device/useScreenSize';
import { earlyAccessLaunchMessages } from '@sorare/core/src/lib/glossary';
import {
  laptopAndAbove,
  tabletAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { PromoHeader } from 'components/earlyAccess/PromoHeader';
import { Countdown } from 'components/market/Countdown';
import EarlyAccessDialog from 'components/market/EarlyAccessDialog';
import {
  earlyAccessEvents,
  earlyAccessFontGradient,
  formatLaunchDate,
  getCurrentEventLaunch,
  isInstantBuyReady,
} from 'lib/earlyAccess';

import earlyAccessDropped from './assets/early_access_dropped.jpg';

const Root = styled(Vertical)`
  padding-top: var(--double-unit);
`;

export const Wrapper = styled(Vertical).attrs({ gap: 2 })`
  align-items: stretch;
  width: 100%;
  @media ${tabletAndAbove} {
    flex-direction: row;
    justify-content: space-between;
  }
`;

const Block = styled(Vertical)`
  width: 100%;
  box-shadow: inset 0 0 0 1px rgba(var(--c-rgb-neutral-1000), 0.16);
  border-radius: var(--unit);
  overflow: hidden;
  flex-grow: 0;
  height: 330px;
  @media ${tabletAndAbove} {
    height: 430px;
  }
`;
const BlockContainer = styled(Vertical)<{ $background: string }>`
  width: 100%;
  background: url(${props => props.$background});
  background-size: cover;
  background-position: center;
  padding: var(--triple-unit) var(--double-unit);
  justify-content: space-between;
  flex-grow: 0;
  height: 100%;
`;

const Image = styled.img`
  max-width: 130px;
  @media ${tabletAndAbove} {
    max-width: 170px;
  }
  @media ${laptopAndAbove} {
    max-width: 210px;
  }
`;

const Title = styled(BodyS)`
  ${earlyAccessFontGradient}
`;

const Capitalize = styled.span`
  text-transform: capitalize;
`;

export const EarlyAccess = () => {
  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const [open, setOpen] = useState<boolean>(false);
  const { locale } = useIntlContext();

  const currentEvent = getCurrentEventLaunch();

  const displayedEvents = useMemo(() => {
    if (isLaptopAndAbove) {
      return earlyAccessEvents;
    }
    return [
      currentEvent,
      ...earlyAccessEvents.filter(
        e => !currentEvent || e.slug !== currentEvent.slug
      ),
    ].filter(Boolean);
  }, [currentEvent, isLaptopAndAbove]);

  return (
    <Root gap={0.5}>
      <Horizontal>
        <Title bold uppercase>
          <FormattedMessage {...earlyAccessLaunchMessages.newSeason} />
        </Title>
        <IconButton
          smaller
          color="transparent"
          icon={faInfoCircle}
          onClick={() => setOpen(true)}
        />
      </Horizontal>
      <Wrapper>
        <ConditionalWrapper
          wrap={!isLaptopAndAbove}
          Wrapper={({ children }: { children: ReactNode }) => (
            <Scrollable contained itemToDisplay={1}>
              {children}
            </Scrollable>
          )}
        >
          {displayedEvents.map(e => {
            const isLaunched = isPast(e.startDate);
            const isCurrentLaunch = getCurrentEventLaunch()?.slug === e.slug;
            const marketUrl = `${isInstantBuyReady(e.startDate) ? FOOTBALL_MARKET_INSTANT_BUY : FOOTBALL_MARKET_NEW_SIGNINGS}?${buildFilterQuery({ [e.searchParams]: true })}`;
            return (
              <Block key={e.slug} gap={0}>
                {isLaunched && <PromoHeader endDate={e.endDate} />}
                <BlockContainer
                  $background={
                    isCurrentLaunch
                      ? earlyAccessDropped
                      : e.backgroundWithPlayers
                  }
                  center
                >
                  <Chip
                    size="small"
                    color="gray"
                    iconLeft={() => <FontAwesomeIcon icon={faParachuteBox} />}
                    label={
                      isLaunched ? (
                        <FormattedMessage
                          {...earlyAccessLaunchMessages.launched}
                        />
                      ) : (
                        <Capitalize>
                          {formatLaunchDate(e.startDate, locale)}
                        </Capitalize>
                      )
                    }
                    custom={{
                      color: 'var(--c-neutral-1000)',
                      background: 'rgba(var(--c-rgb-neutral-1000), .16)',
                    }}
                  />

                  <ConditionalWrapper
                    Wrapper={({ children }: { children: ReactNode }) => (
                      <ButtonBase to={marketUrl}>{children}</ButtonBase>
                    )}
                    wrap={isLaunched}
                  >
                    <Image src={e.cards} />
                  </ConditionalWrapper>
                  {isLaunched ? (
                    <Button to={marketUrl} color="secondary" size="medium">
                      <FormattedMessage
                        id="forYou.earlyAccess.viewCards"
                        defaultMessage="Discover new cards"
                      />
                    </Button>
                  ) : (
                    <Countdown expanded={false} endDate={e.startDate} />
                  )}
                </BlockContainer>
              </Block>
            );
          })}
        </ConditionalWrapper>
      </Wrapper>
      <EarlyAccessDialog open={open} onClose={() => setOpen(false)} />
    </Root>
  );
};
